import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Car, Coffee, Wrench, ShoppingBag, TrendingUp, Calendar, Mail, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import TaxYearExport from "@/components/TaxYearExport";

interface CategorySummary {
  name: string;
  amount: number;
  icon: any;
  color: string;
  percentage: number;
}

const categoryIcons: Record<string, any> = {
  Gas: Car,
  Meals: Coffee,
  Repairs: Wrench,
  Supplies: ShoppingBag,
};

const categoryColors: Record<string, string> = {
  Gas: "bg-blue-500",
  Meals: "bg-orange-500",
  Repairs: "bg-purple-500",
  Supplies: "bg-green-500",
};

export default function Summary() {
  const { toast } = useToast();
  const [categoryData, setCategoryData] = useState<CategorySummary[]>([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  useEffect(() => {
    fetchExpenses();
  }, [selectedMonth]);

  const fetchExpenses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().split('T')[0];

      const { data, error } = await supabase
        .from("receipts")
        .select("category, total, amount")
        .eq("user_id", user.id) // Explicit user_id check for security
        .eq("status", "approved")
        .gte("date", startDate)
        .lte("date", endDate);

      if (error) throw error;

      const receipts = data || [];
      const total = receipts.reduce((sum, r) => sum + (r.total || r.amount), 0);
      
      // Group by category
      const categoryTotals: Record<string, number> = {};
      receipts.forEach(receipt => {
        const cat = receipt.category || "Other";
        categoryTotals[cat] = (categoryTotals[cat] || 0) + (receipt.total || receipt.amount);
      });

      const categories = Object.entries(categoryTotals).map(([name, amount]) => ({
        name,
        amount,
        icon: categoryIcons[name] || ShoppingBag,
        color: categoryColors[name] || "bg-gray-500",
        percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      }));

      setCategoryData(categories);
      setTotalExpenses(total);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error fetching expenses:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendEmailReport = async () => {
    setSendingEmail(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const { data, error } = await supabase.functions.invoke('send-expense-report', {
        body: { month, year }
      });

      if (error) throw error;

      toast({
        title: "Report Sent",
        description: "Your expense report has been sent to your email.",
      });
    } catch (error: any) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to send email report:", error);
      }
      toast({
        title: "Error",
        description: "Failed to send report",
        variant: "destructive",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="w-full sm:w-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Expense Summary</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Overview of your categorized expenses</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-full sm:w-[180px] md:w-[200px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-50 bg-background">
              {getMonthOptions().map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={sendEmailReport} 
            disabled={sendingEmail || categoryData.length === 0}
            className="flex-1 sm:flex-none whitespace-nowrap"
            size="default"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sendingEmail ? "Sending..." : "Email Report"}
          </Button>
          <Button 
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            className="flex-1 sm:flex-none whitespace-nowrap"
            size="default"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Tax Year
          </Button>
        </div>
          </div>

          {loading ? (
            <Card className="p-8 sm:p-12 text-center">
              <p className="text-muted-foreground">Loading expenses...</p>
            </Card>
          ) : categoryData.length === 0 ? (
            <Card className="p-8 sm:p-12 text-center">
              <p className="text-sm sm:text-base text-muted-foreground">No expenses for this month. Upload some receipts to get started!</p>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-full">
            <Card className="p-4 sm:p-6 bg-gradient-primary">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-white/90">Total Expenses</p>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-white break-words">${totalExpenses.toFixed(2)}</p>
            </Card>

            {categoryData.map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.name} className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">{category.name}</p>
                  </div>
                  <p className="text-xl sm:text-2xl font-bold text-foreground break-words">${category.amount.toFixed(2)}</p>
                </Card>
              );
            })}
              </div>

              <Card className="p-4 sm:p-6 max-w-full overflow-hidden">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4 sm:mb-6">Expense Breakdown</h3>
        <div className="space-y-4 max-w-full">
          {categoryData.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.name} className="max-w-full">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground truncate">{category.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs sm:text-sm font-semibold text-foreground whitespace-nowrap">${category.amount.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{category.percentage}%</p>
                  </div>
                </div>
                <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                  <div
                    className={`${category.color} h-2 rounded-full transition-all`}
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-gradient-subtle max-w-full overflow-hidden">
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2">Tax Insights</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">
              Based on your expenses, you may be eligible for deductions in the following categories:
            </p>
            <ul className="space-y-2 max-w-full">
              {(() => {
                const vehicleExpenses = (categoryData.find(c => c.name === "Gas")?.amount || 0) + 
                                       (categoryData.find(c => c.name === "Repairs")?.amount || 0);
                const mealsExpenses = categoryData.find(c => c.name === "Meals")?.amount || 0;
                const suppliesExpenses = categoryData.find(c => c.name === "Supplies")?.amount || 0;
                
                return (
                  <>
                    {vehicleExpenses > 0 && (
                      <li className="flex items-start gap-2 text-xs sm:text-sm">
                        <span className="text-primary flex-shrink-0">•</span>
                        <span className="text-foreground break-words">Vehicle expenses (Gas & Repairs): ${vehicleExpenses.toFixed(2)}</span>
                      </li>
                    )}
                    {mealsExpenses > 0 && (
                      <li className="flex items-start gap-2 text-xs sm:text-sm">
                        <span className="text-primary flex-shrink-0">•</span>
                        <span className="text-foreground break-words">Business meals: ${mealsExpenses.toFixed(2)}</span>
                      </li>
                    )}
                    {suppliesExpenses > 0 && (
                      <li className="flex items-start gap-2 text-xs sm:text-sm">
                        <span className="text-primary flex-shrink-0">•</span>
                        <span className="text-foreground break-words">Office supplies: ${suppliesExpenses.toFixed(2)}</span>
                      </li>
                    )}
                    {vehicleExpenses === 0 && mealsExpenses === 0 && suppliesExpenses === 0 && (
                      <li className="flex items-start gap-2 text-xs sm:text-sm">
                        <span className="text-muted-foreground break-words">No tax-deductible expenses found for this month.</span>
                      </li>
                     )}
                  </>
                );
                  })()}
                </ul>
              </Card>
            </>
          )}

          <TaxYearExport
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
          />
    </div>
  );
}
